import React, { useEffect, useState, useMemo } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ShoppingBag, Check, ShieldCheck, ArrowLeft, Loader2, Clock, Package } from 'lucide-react'
import { apiGet } from '../lib/api'
import { useCart } from '../context/CartContext'
import { useSiteConfig } from '../context/SiteConfigContext'
import { isGstEnabled } from '../lib/companyGst'
import { assetSrc } from '../lib/assets'
import { StorefrontLayout } from '../layouts/StorefrontLayout'
import { ProductImage } from '../components/ProductImage'
import { ProductReviews } from '../components/ProductReviews'
import type { Product, ProductVariant } from '@buddys-choice/shared'

function parseQty(value: string): number {
  const n = parseInt(value.replace(/\D/g, ''), 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

function variantPrice(v: ProductVariant | null, basePrice: number): number {
  if (!v) return basePrice
  return v.priceOverride ?? (v as { price_override?: number | null }).price_override ?? basePrice
}

function variantStock(v: ProductVariant | null): number {
  if (!v) return 0
  return v.stockQty ?? Number((v as { stock_qty?: number }).stock_qty ?? 0)
}

export const ProductPage: React.FC = () => {
  const { productId } = useParams({ from: '/product/$productId' })
  const navigate = useNavigate()
  const { addItem } = useCart()
  const { config } = useSiteConfig()
  const gstOn = isGstEnabled(config)

  const [product, setProduct] = useState<Product | null>(null)
  const [variants, setVariants] = useState<ProductVariant[]>([])
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [added, setAdded] = useState(false)
  const [variantError, setVariantError] = useState('')

  const galleryImages = useMemo(() => {
    if (!product) return [] as string[]
    const fromGallery = (product.images ?? []).map((img) => img.url).filter(Boolean)
    if (fromGallery.length > 0) return fromGallery
    if (product.heroImageUrl) return [product.heroImageUrl]
    return []
  }, [product])

  const activeImage = galleryImages[selectedImageIndex] ?? galleryImages[0] ?? null

  useEffect(() => {
    if (!productId) return
    setLoading(true)
    setError('')
    setSelectedImageIndex(0)
    apiGet<Product>(`products/${productId}`)
      .then((prod) => {
        setProduct(prod)
        return apiGet<ProductVariant[]>('variants', { productId })
      })
      .then((vars) => {
        setVariants(vars || [])
        if (vars?.length) setSelectedVariant(vars[0]!)
      })
      .catch((err: unknown) => {
        setError((err as Error).message || 'Product not found.')
      })
      .finally(() => setLoading(false))
  }, [productId])

  const stockQty = variants.length === 0 ? 999 : variantStock(selectedVariant)
  const isPreOrder = stockQty <= 0
  const maxQty = isPreOrder ? 99 : stockQty

  useEffect(() => {
    setQuantity((q) => Math.min(Math.max(1, q), maxQty))
  }, [maxQty, selectedVariant?.id])

  if (loading) {
    return (
      <StorefrontLayout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--gold-main)]" />
        </div>
      </StorefrontLayout>
    )
  }

  if (error || !product) {
    return (
      <StorefrontLayout>
        <div className="max-w-lg mx-auto px-4 py-20 text-center space-y-4">
          <p className="text-[var(--text-muted)]">{error || 'Product not found.'}</p>
          <Link to="/" className="btn-gold text-sm inline-flex">Back to Store</Link>
        </div>
      </StorefrontLayout>
    )
  }

  const currentPrice = variantPrice(selectedVariant, product.basePrice)
  const needsVariant = variants.length > 0 && !selectedVariant

  const selectedComparePrice = (selectedVariant as { compareAtPrice?: number | null; compare_at_price?: number | null } | null)?.compareAtPrice ?? (selectedVariant as { compare_at_price?: number | null } | null)?.compare_at_price
  const prodComparePrice = (product as { compareAtPrice?: number | null; compare_at_price?: number | null }).compareAtPrice ?? (product as { compare_at_price?: number | null }).compare_at_price
  const rawComparePrice = selectedComparePrice ?? prodComparePrice
  const compareAtPrice = rawComparePrice && rawComparePrice > currentPrice
    ? rawComparePrice
    : currentPrice > 0
    ? Math.round(currentPrice * 1.25)
    : null

  const hasDiscount = compareAtPrice != null && compareAtPrice > currentPrice
  const discountAmount = hasDiscount ? compareAtPrice - currentPrice : 0
  const discountPercent = hasDiscount ? Math.round((discountAmount / compareAtPrice) * 100) : 0

  const formattedPrice = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(currentPrice)

  const formattedComparePrice = hasDiscount && compareAtPrice
    ? new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(compareAtPrice)
    : null

  const handleAddToCart = () => {
    setVariantError('')
    if (variants.length > 0 && !selectedVariant) {
      setVariantError('Please select a variant before continuing.')
      return
    }

    const variantDesc = selectedVariant?.attributes
      ?.map((a) => `${a.attrTypeName || 'Attr'}: ${a.attrValue}`)
      .join(', ')

    addItem({
      productId: product.id,
      variantId: selectedVariant?.id,
      productName: product.name,
      productImage: activeImage || product.heroImageUrl || undefined,
      variantDescription: variantDesc,
      unitPrice: currentPrice,
      quantity,
      stockQty: isPreOrder ? 0 : stockQty,
      isPreOrder,
    })
    setAdded(true)
  }

  return (
    <StorefrontLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <button
          onClick={() => navigate({ to: '/' })}
          className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to catalogue
        </button>

        <div className="glass-luxury-card overflow-hidden grid grid-cols-1 md:grid-cols-2">
          <div className="relative bg-[var(--bg-main)] border-b md:border-b-0 md:border-r border-[var(--border)] flex flex-col justify-between">
            <div className="w-full h-full min-h-[400px] sm:min-h-[500px] lg:min-h-[560px] relative overflow-hidden">
              <ProductImage
                src={activeImage}
                alt={product.name}
                className="w-full h-full"
                imgClassName="w-full h-full object-cover transition-all duration-500 hover:scale-105"
              />
            </div>

            {galleryImages.length > 1 && (
              <div className="p-4 flex gap-2 overflow-x-auto bg-[var(--bg-surface-2)]/60 border-t border-[var(--border)]">
                {galleryImages.map((url, index) => (
                  <button
                    key={`${url}-${index}`}
                    type="button"
                    onClick={() => setSelectedImageIndex(index)}
                    className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${
                      selectedImageIndex === index
                        ? 'border-[var(--gold-main)] ring-2 ring-[var(--gold-main)]/30'
                        : 'border-[var(--border)] hover:border-[var(--gold-main)]/50'
                    }`}
                  >
                    <img src={assetSrc(url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <span className="badge badge-gold uppercase text-[10px]">{product.category}</span>
              <h1 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] mt-2">
                {product.name}
              </h1>
              <p className="text-sm text-[var(--text-muted)] mt-2">{product.description}</p>
              
              <div className="flex flex-wrap items-baseline gap-3 mt-4">
                <span className="text-2xl sm:text-3xl font-title font-bold text-[var(--gold-main)]">{formattedPrice}</span>
                {formattedComparePrice && (
                  <span className="text-base font-mono text-[var(--text-muted)] line-through decoration-red-400 decoration-2 font-semibold">
                    {formattedComparePrice}
                  </span>
                )}
                {discountPercent > 0 && (
                  <span className="badge bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs px-2.5 py-1 font-mono font-bold">
                    {discountPercent}% OFF (SAVE ₹{discountAmount.toLocaleString('en-IN')})
                  </span>
                )}
              </div>
              {gstOn && (
                <p className="text-[10px] text-[var(--text-faint)]">Excl. 3% CGST + 3% SGST</p>
              )}
              {!isPreOrder && stockQty < 999 && (
                <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
                  <Package className="w-3.5 h-3.5" />
                  {stockQty} in stock — ready to ship
                </p>
              )}
            </div>

            {isPreOrder && (
              <div className="flex items-start gap-2 rounded-xl border border-[var(--gold-soft-border)] bg-[var(--gold-soft-bg)] p-3 text-xs text-[var(--text-muted)]">
                <Clock className="w-4 h-4 text-[var(--gold-main)] shrink-0 mt-0.5" />
                <p>
                  <span className="font-semibold text-[var(--text-primary)]">Pre-order only.</span>{' '}
                  This variant is currently out of stock. Reserve now — we&apos;ll craft and ship once ready.
                </p>
              </div>
            )}

            {variants.length > 0 && (
              <div className="space-y-2">
                <label className="form-label">Select variant *</label>
                <div className="space-y-2">
                  {variants.map((v) => {
                    const isSelected = selectedVariant?.id === v.id
                    const vStock = variantStock(v)
                    const label =
                      v.attributes?.map((a) => `${a.attrTypeName}: ${a.attrValue}`).join(' · ') ||
                      v.sku
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => {
                          setSelectedVariant(v)
                          setVariantError('')
                          setAdded(false)
                        }}
                        className={`w-full text-left p-3 rounded-xl border text-xs transition ${
                          isSelected
                            ? 'border-[var(--gold-main)] bg-[var(--gold-soft-bg)]'
                            : 'border-[var(--border)] hover:border-[var(--gold-main)]/50'
                        }`}
                      >
                        <span className="font-medium text-[var(--text-primary)]">{label}</span>
                        <span className="block text-[10px] text-[var(--text-faint)] mt-0.5 font-mono">
                          SKU: {v.sku} · {vStock > 0 ? `${vStock} in stock` : 'Pre-order'}
                        </span>
                      </button>
                    )
                  })}
                </div>
                {variantError && <p className="form-error">{variantError}</p>}
              </div>
            )}

            <div className="flex items-center justify-between border-t border-[var(--border)] pt-4 gap-3">
              <div className="flex items-center gap-2">
                <label htmlFor="product-qty" className="text-xs text-[var(--text-muted)] shrink-0">
                  Qty
                </label>
                <div className="flex items-center border border-[var(--border)] rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="px-3 py-2 hover:bg-[var(--bg-surface-2)] text-sm font-bold"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <input
                    id="product-qty"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.min(parseQty(e.target.value), maxQty))}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '')
                    }}
                    className="w-12 text-center font-semibold bg-transparent border-x border-[var(--border)] py-2 text-sm outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    className="px-3 py-2 hover:bg-[var(--bg-surface-2)] text-sm font-bold"
                    aria-label="Increase quantity"
                    disabled={quantity >= maxQty}
                  >
                    +
                  </button>
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={added || needsVariant}
                className="btn-gold text-sm py-3 px-6 flex items-center gap-2 disabled:opacity-50"
              >
                {added ? (
                  <>
                    <Check className="w-4 h-4" /> Added
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" /> {isPreOrder ? 'Pre-order Now' : 'Add to Bag'}
                  </>
                )}
              </button>
            </div>

            <p className="text-[11px] text-[var(--text-faint)] flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-[var(--gold-main)]" />
              1-year color warranty · Insured COD delivery
            </p>
          </div>
        </div>

        <ProductReviews productId={product.id} />
      </div>
    </StorefrontLayout>
  )
}
